import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTheme } from "../../lib/theme";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { clsx } from "clsx";

const SIDEBAR_COLLAPSED_KEY = "amigo-store-sidebar-collapsed";

export default function AppShell() {
  const { theme } = useTheme();
  const location = useLocation();
  const isDark = theme === "dark";

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Persist collapsed state
  const toggleCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  };

  const sidebarWidth = sidebarCollapsed ? 72 : 260;

  return (
    <div className={clsx("min-h-screen transition-colors", isDark ? "bg-gray-950" : "bg-gray-50")}>
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleCollapse}
      />

      {/* Main content area */}
      <div
        className="transition-all duration-300 ease-in-out min-h-screen flex flex-col"
        style={{ marginLeft: `${sidebarWidth}px` }}
      >
        {/* Responsive: remove margin on mobile */}
        <style>{`
          @media (max-width: 1023px) {
            [style*="margin-left"] {
              margin-left: 0 !important;
            }
          }
        `}</style>

        {/* Topbar */}
        <Topbar
          onMenuToggle={() => setSidebarOpen((p) => !p)}
          sidebarCollapsed={sidebarCollapsed}
        />

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer
          className={clsx(
            "px-6 py-4 border-t text-center",
            isDark ? "border-white/5 text-white/20" : "border-gray-200 text-gray-300"
          )}
        >
          <p className="text-xs">
            <span className="font-semibold">
              <span className="text-amigo-orange">ami</span>
              <span className="text-amigo-teal">Go</span>
            </span>
            {" "}Store Dashboard &copy; {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </div>
  );
}
